import { useEffect, useState } from 'react';
import PolicyPauseDayInfoView from '@/dashboard/components/Products/policyCard/dialogs/pausePolicy/policyPauseDayInfo';
import { usePolicyPauseOverviewInfo } from '@/dashboard/components/Products/policyCard/hooks/usePolicyPauseOverviewInfo';
import PolicyModel from '@/dashboard/models/policys/PolicyModel';
import { DatePickerControlled } from '@/dashboard/mui/form/DatePicker';
import { ClockIcon } from '@heroicons/react/outline';
import { InformationCircleIcon } from '@heroicons/react/outline';
import { calculateTimeBetweenDates, judgeWhetherIsBetweenTwoDate } from '@jove-frontend/uikit';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Typography } from '@mui/material';
import { Dayjs } from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';

interface ExtendPauseDialogFormViewProps {
  policy: PolicyModel;
  close: () => void;
  confirm: ({ time }) => void;
  endDate?: Dayjs;
}

const ExtendPauseDialogFormView = (props: ExtendPauseDialogFormViewProps) => {
  const { policy, close, confirm, endDate } = props;
  const methods = useForm({
    defaultValues: { endDate: endDate || policy.pauseEndDateDayjs },
  });
  const { handleSubmit, register, getValues, control } = methods;
  const { timeInfoLoading, policyRemaingTimeInfo } = usePolicyPauseOverviewInfo(policy?.id);
  const [extendMoreDays, setExtendMoreDays] = useState(0);

  const closeAction = () => {
    if (close) close();
  };

  /**
   * mounted
   */
  useEffect(() => {
    timeChangeAction(getValues('endDate'));
  }, []);

  /**
   * methods
   */
  // check success
  const checkFormSuccess = () => {
    if (confirm) confirm({ time: getValues('endDate') });
  };

  // click unpause
  const extendPauseAction = () => {
    handleSubmit(checkFormSuccess)();
  };

  // time change
  const timeChangeAction = (value) => {
    const days = calculateTimeBetweenDates(value, policy.pauseEndDateDayjs);
    setExtendMoreDays(days >= 0 ? days : 0);
  };

  // end time validate
  const endTimeValidate = (time) => {
    return judgeWhetherIsBetweenTwoDate(time, policy.pauseEndDateDayjs, policy.policyEndDateDayjs) ? true : 'Please select a valid date';
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Box className="mt-4 flex flex-col items-center">
          <Box className="bg-brand-secondary/20 text-brand-secondary rounded-full p-2">
            <ClockIcon className="h-7 w-7" />
          </Box>
          <Typography className="text-black-secondary mt-4 font-black" variant="h5">
            Extend pause
          </Typography>
          <PolicyPauseDayInfoView className="mt-7" info={policyRemaingTimeInfo} type={2} />
          <Typography className="text-black-secondary mt-4 font-bold" variant="subtitle1">
            What is your pausing end date?
          </Typography>
          <DatePickerControlled
            name="endDate"
            control={control}
            {...register('endDate', { required: true, validate: endTimeValidate })}
            minDate={policy.pauseEndDateDayjs}
            maxDate={policy.policyEndDateDayjs}
            label="End date"
            onDateChange={timeChangeAction}
          />
          <Box className="mt-5 flex w-full flex-row items-center space-x-2 rounded-lg bg-black/5 p-4">
            <InformationCircleIcon className="text-brand-primary h-6 w-6 flex-shrink-0" />
            <Typography variant="subtitle2">Your policy will be paused for</Typography>
            <Typography className="text-brand-primary mx-1 inline-block" variant="subtitle2">
              {extendMoreDays} day
              {extendMoreDays > 1 && 's'}
            </Typography>
            <Typography variant="subtitle2">more.</Typography>
          </Box>
          <Box className="mt-6 flex w-full flex-row justify-end">
            <Button className="text-brand-primary text-lg font-bold" variant="text" onClick={closeAction}>
              Cancel
            </Button>
            <LoadingButton className="ml-2 h-12 w-[174px] rounded-full text-lg font-bold" color="primary" onClick={extendPauseAction} loading={timeInfoLoading} variant="contained">
              <span>Next</span>
            </LoadingButton>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};

export default ExtendPauseDialogFormView;
